import { useState } from "react";
import { Table2, Lock } from "lucide-react";
import { viewModes } from "./view-modes";
import { useI18n } from "@/lib/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ContextView {
  id: string;
  name: string;
  type: string;
  isShared: boolean;
}
export function ViewBadge({ view }: { view: ContextView }) {
  const fr = useI18n().language === "fr";
  const mode = viewModes.find((mode) => mode.type === view.type);
  const Icon = mode?.icon ?? Table2;
  return (
    <>
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="truncate">{view.name}</span>
      <span className="text-xs text-foreground/60">
        {fr ? mode?.fr : mode?.en}
      </span>
      {!view.isShared && (
        <Lock className="h-3 w-3 shrink-0 text-foreground/60" />
      )}
    </>
  );
}
export function ViewContextPicker({
  open,
  onClose,
  views,
  selectedIds,
  onSelect,
}: {
  open: boolean;
  onClose(): void;
  views: ContextView[];
  selectedIds: string[];
  onSelect(view: ContextView): void;
}) {
  const fr = useI18n().language === "fr";
  const [mode, setMode] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          onClose();
          setMode(null);
          setSearch("");
        }
      }}
    >
      <DialogContent className="max-h-[80dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {fr ? "Ajouter une vue au contexte" : "Add a view to context"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {viewModes.map((item) => (
            <button
              key={item.type}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${mode === item.type ? "border-primary bg-primary/15" : "border-white/10 hover:bg-white/5"}`}
              onClick={() => setMode(item.type)}
            >
              <item.icon className="h-4 w-4" />
              {fr ? item.fr : item.en}
            </button>
          ))}
        </div>
        {mode && (
          <>
            <input
              className="rounded-lg border border-white/10 bg-white/5 p-2 text-sm"
              placeholder={fr ? "Rechercher une vue…" : "Search views…"}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {views
                .filter(
                  (view) =>
                    view.type === mode &&
                    view.name
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase()),
                )
                .map((view) => (
                  <button
                    key={view.id}
                    disabled={
                      selectedIds.includes(view.id) || selectedIds.length >= 10
                    }
                    className="flex w-full items-center gap-2 rounded-lg p-3 text-left text-sm hover:bg-white/5 disabled:opacity-40"
                    onClick={() => {
                      onSelect(view);
                      onClose();
                      setMode(null);
                      setSearch("");
                    }}
                  >
                    <ViewBadge view={view} />
                  </button>
                ))}
            </div>
            {!views.some((view) => view.type === mode) && (
              <p className="text-sm text-foreground/60">
                {fr ? "Aucune vue dans ce mode." : "No views in this mode."}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
